import { Injectable, inject } from '@angular/core';

import { resolveAttachmentEntityId } from '../../shared/utils/attachment-target';
import { settledCache } from '../../shared/utils/cache-ready';
import { ContractService } from '../entity-services/contract.service';
import { PartyService } from '../entity-services/party.service';
import { BaaService } from '../entity-services/baa.service';
import { CoiService } from '../entity-services/coi.service';
import { EntityType } from '../../objects/interface-library';
import {
  IntakeAnalysisResponse,
  IntakeDocType,
  PartyMatch,
} from '../ai/document-analysis.service';

/**
 * Turns "the AI thinks this document belongs to Party X" into "here are the
 * records you can actually file it against."
 *
 * Backs AttachDocumentDialogComponent's suggestion step. All reads come from the
 * in-memory entity caches — no new backend calls — so this is cheap enough to
 * re-run whenever the user swaps the party.
 *
 * ── The join, and why it differs per type ──────────────────────────────────
 * Contracts reference a party structurally: `directVendor` / `otherParties[]`
 * carry a `vendorNum`, so ContractService.contractsForParty() answers exactly.
 *
 * BAAs and COIs do NOT. On records written by the current forms,
 * `businessAssociateName` and `insuredParty` are free-text strings, not party
 * refs (older hydrated records sometimes hold an object with `.name`, which is
 * why the readers below tolerate both). So there is no reliable id-to-id join.
 * Instead we go party → its contracts → BAAs/COIs whose `contracts[]` includes
 * one of those, and fall back to a normalized name match against the party's
 * name. Both signals are conservative: a miss means "we don't offer it here"
 * and the user picks manually, never a wrong suggestion presented as fact.
 */

/** One offerable destination for an attachment. */
export interface AttachmentTargetOption {
  /** The raw entity, already stamped with `entityType` so it can go straight to applyAttachmentTarget(). */
  entity: any;
  entityType: EntityType;
  /** What the user sees. */
  label: string;
  /** Secondary line (contract number, dates, insured party…). May be empty. */
  sublabel: string;
  /** Stable key for radio-group binding and preselection comparison. */
  key: string;
  /** Past its expiration date. Rendered with a chip so the choice stays informed. */
  expired?: boolean;
}

/** Everything attachable that we could tie to one party. */
export interface PartyAttachmentTargets {
  party: any;
  partyOption: AttachmentTargetOption;
  contracts: AttachmentTargetOption[];
  baas: AttachmentTargetOption[];
  cois: AttachmentTargetOption[];
}

/** A party the AI matched, with its confidence, ready to show in the header. */
export interface ResolvedPartyMatch {
  party: any;
  /** 0..1 similarity from the backend's fuzzy match. Meaningless when `userChosen`. */
  similarity: number;
  matchConfidence: PartyMatch['matchConfidence'];
  /** The name as it appeared in the document (may differ from the HC record). */
  extractedName: string;
  /**
   * The user searched for this party rather than the AI matching it. The header
   * must not show a confidence score for these — a hand-picked party isn't a
   * "100% match", and labelling it as one would misrepresent a human choice as
   * a machine result.
   */
  userChosen?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AttachmentTargetsService {
  private contractService = inject(ContractService);
  private partyService = inject(PartyService);
  private baaService = inject(BaaService);
  private coiService = inject(CoiService);

  // ── Party resolution ────────────────────────────────────────────────

  /**
   * Resolve the AI's party candidates against the real Vendors directory,
   * best first.
   *
   * Only `entityType === 'vendor'` candidates are usable: legal-entity matches
   * point at the customer's own organization, which isn't an attachable party
   * here. Candidates whose vendorNum isn't in the local cache are dropped — the
   * backend matched against the same table, but a stale cache shouldn't produce
   * an option that can't be turned into a real record.
   */
  async resolvePartyMatches(partyMatches: PartyMatch[] | undefined): Promise<ResolvedPartyMatch[]> {
    if (!partyMatches?.length) return [];

    const parties = await this.parties();
    const byVendorNum = new Map<string, any>();
    for (const p of parties) {
      const num = this.str(p?.vendorNum);
      if (num) byVendorNum.set(num, p);
    }

    const seen = new Set<string>();
    const out: ResolvedPartyMatch[] = [];

    for (const match of partyMatches) {
      for (const candidate of match.candidates ?? []) {
        if (candidate.entityType !== 'vendor') continue;
        const num = this.str(candidate.vendorNum);
        if (!num || seen.has(num)) continue;
        const party = byVendorNum.get(num);
        if (!party) continue;
        seen.add(num);
        out.push({
          party,
          similarity: candidate.similarity ?? 0,
          matchConfidence: match.matchConfidence,
          extractedName: match.extractedName ?? '',
        });
      }
    }

    return out.sort((a, b) => b.similarity - a.similarity);
  }

  // ── Targets for a party ─────────────────────────────────────────────

  /**
   * Everything the given party can host an attachment on.
   *
   * Contracts include expired ones (`includeExpired: true`) — an addendum or a
   * closing invoice frequently lands on a contract that has just run out, and
   * hiding it would make the document unfileable. They're flagged so the UI can
   * mark them.
   */
  async targetsForParty(party: any): Promise<PartyAttachmentTargets> {
    const vendorNum = this.str(party?.vendorNum);
    const parentNum = this.str(party?.parentVendor);

    // contractsForParty() reads visibleContractsSubject synchronously, so it has
    // to be called against a settled cache — mid-pagination it would quietly
    // return only the contracts that happen to have loaded so far. Settle on
    // visibleContracts$ specifically, not allContracts$: visible is derived from
    // allContracts + permissions + user email, so settling the source alone
    // would leave the derived list free to change right after the await.
    await settledCache(this.contractService.visibleContracts$);
    const contracts = this.contractService.contractsForParty([vendorNum, parentNum], true);
    const contractOptions = contracts
      .map((c) => this.contractOption(c))
      .filter((option) => this.isAttachable(option));

    // The set of contract identifiers this party owns, in both flavors — a
    // BAA/COI `contracts[]` entry may hold either a contractId or a contractNum.
    const contractKeys = new Set<string>();
    for (const c of contracts) {
      const id = this.str((c as any).contractId);
      const num = this.str((c as any).contractNum);
      if (id) contractKeys.add(id);
      if (num) contractKeys.add(num);
    }

    const partyName = this.normalizeName(party?.name);
    const [allBaas, allCois] = await Promise.all([
      settledCache(this.baaService.allBaas$),
      settledCache(this.coiService.allCois$),
    ]);

    // isAttachable() drops records that would render as a normal choice and then
    // fail at save time. A BAA can carry a baaNum without a baaId (both optional
    // on the interface), and it's baaId the attachment links against — offering
    // one is offering a dead end.
    const baas = (allBaas ?? [])
      .filter((b) => this.linkedToParty(b, contractKeys, partyName, ['businessAssociateName', 'coveredEntity']))
      .map((b) => this.baaOption(b))
      .filter((option) => this.isAttachable(option));

    const cois = (allCois ?? [])
      .filter((c) => this.linkedToParty(c, contractKeys, partyName, ['insuredParty', 'insuredPartyFriendlyName']))
      .map((c) => this.coiOption(c))
      .filter((option) => this.isAttachable(option));

    return {
      party,
      partyOption: this.partyOption(party),
      contracts: contractOptions,
      baas,
      cois,
    };
  }

  // ── Pre-selection ───────────────────────────────────────────────────

  /**
   * The single option to pre-select, or null to leave the user to choose.
   *
   * This is a suggestion, never an action — the dialog always requires an
   * explicit save. When we can't narrow a group to one obvious answer we
   * deliberately return null rather than guessing the first row: a wrong
   * pre-selection that the user doesn't notice is worse than no pre-selection.
   */
  preselect(
    classification: IntakeDocType | undefined,
    analysis: IntakeAnalysisResponse | null,
    targets: PartyAttachmentTargets
  ): AttachmentTargetOption | null {
    switch (classification) {
      case 'addendum': {
        const reference = analysis?.extraction?.addendumFields?.parentDocumentReference;
        const referenced = this.bestContractForReference(reference, targets.contracts);
        if (referenced) return referenced;
        return targets.contracts.length === 1 ? targets.contracts[0] : null;
      }
      case 'baa':
        return targets.baas.length === 1 ? targets.baas[0] : null;
      case 'coi':
        return targets.cois.length === 1 ? targets.cois[0] : null;
      case 'contract':
      case 'unknown':
      default:
        // A whole contract (or something we couldn't classify) belongs on the
        // party itself — filing a contract under another contract is almost
        // always wrong, and the party is always a safe home.
        return targets.partyOption;
    }
  }

  /**
   * Match an addendum's "parent document" reference against the party's
   * contracts. Requires a decisive winner: if two contracts score the same the
   * reference wasn't specific enough to act on, so we return null and let the
   * user pick.
   */
  private bestContractForReference(
    reference: string | undefined,
    contracts: AttachmentTargetOption[]
  ): AttachmentTargetOption | null {
    const ref = this.normalizeName(reference);
    if (!ref || contracts.length === 0) return null;

    const scored = contracts
      .map((option) => ({ option, score: this.referenceScore(ref, option) }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score);

    if (scored.length === 0) return null;
    if (scored.length > 1 && scored[0].score === scored[1].score) return null;
    return scored[0].option;
  }

  private referenceScore(ref: string, option: AttachmentTargetOption): number {
    const name = this.normalizeName(option.label);
    const num = this.normalizeName(option.entity?.contractNum);
    const id = this.normalizeName(option.entity?.contractId);

    // An explicit contract number/id in the reference is the strongest signal —
    // it's an identifier, not prose, so a containment hit is unambiguous.
    if (num && ref.includes(num)) return 100;
    if (id && ref.includes(id)) return 100;
    if (name && (ref.includes(name) || name.includes(ref))) return 50;

    // Otherwise fall back to shared significant words, so "Amendment to the
    // Acme Staffing Agreement" still finds "Acme Staffing Agreement".
    const refWords = new Set(ref.split(' ').filter((w) => w.length > 3));
    const nameWords = name.split(' ').filter((w) => w.length > 3);
    if (refWords.size === 0 || nameWords.length === 0) return 0;
    return nameWords.filter((w) => refWords.has(w)).length;
  }

  // ── Option builders ─────────────────────────────────────────────────

  partyOption(party: any): AttachmentTargetOption {
    const num = this.str(party?.vendorNum);
    return {
      entity: { ...party, entityType: EntityType.Party },
      entityType: EntityType.Party,
      label: party?.name || num || 'Unnamed party',
      sublabel: num ? `Party #${num}` : '',
      key: `party:${num}`,
    };
  }

  contractOption(contract: any): AttachmentTargetOption {
    const id = this.str(contract?.contractId);
    const num = this.str(contract?.contractNum);
    const expired = this.isExpired(contract?.expirationDate);
    const parts = [num ? `#${num}` : '', contract?.contractType || ''].filter(Boolean);
    return {
      entity: { ...contract, entityType: EntityType.Contract },
      entityType: EntityType.Contract,
      label: contract?.nameOfContract || contract?.friendlyName || id || 'Untitled contract',
      sublabel: parts.join(' · '),
      key: `contract:${id || num}`,
      expired,
    };
  }

  baaOption(baa: any): AttachmentTargetOption {
    const id = this.str(baa?.baaId);
    const num = this.str(baa?.baaNum);
    const associate = this.readName(baa?.businessAssociateName);
    return {
      // `name` is what getAttachmentEntityDisplayName() reads for a BAA, and BAA
      // records don't carry one — seed it from the business-associate name so the
      // attachments grid shows something meaningful instead of a bare id.
      entity: { ...baa, name: baa?.name || associate || num, entityType: EntityType.Baa },
      entityType: EntityType.Baa,
      label: associate || `BAA ${num || id}`,
      sublabel: num ? `BAA #${num}` : '',
      key: `baa:${id || num}`,
      expired: this.isExpired(baa?.expirationDate),
    };
  }

  coiOption(coi: any): AttachmentTargetOption {
    const id = this.str(coi?.coiId);
    const num = this.str(coi?.coiNum);
    const insured = coi?.insuredPartyFriendlyName || this.readName(coi?.insuredParty);
    const carrier = coi?.insuranceProviderNameDisplay || '';
    return {
      // Same reasoning as baaOption(): give the record a `name` for the grid.
      entity: { ...coi, name: coi?.name || insured || num, entityType: EntityType.Coi },
      entityType: EntityType.Coi,
      label: insured || `COI ${num || id}`,
      sublabel: [num ? `COI #${num}` : '', carrier].filter(Boolean).join(' · '),
      key: `coi:${id || num}`,
      expired: this.isExpired(coi?.expirationDate),
    };
  }

  // ── Internals ───────────────────────────────────────────────────────

  /**
   * Can an attachment actually be linked to this option? Uses the same resolver
   * the save path does, so a row can never be offered that Attach would reject.
   */
  isAttachable(option: AttachmentTargetOption): boolean {
    return resolveAttachmentEntityId(option.entity) !== null;
  }

  /**
   * The party directory, waited out to completion. cbhi carries ~4,500 vendors
   * streamed in ~45 pages, and a vendorNum the AI matched is as likely to be on
   * page 40 as page 1 — reading mid-stream would drop real matches on the floor.
   */
  private async parties(): Promise<any[]> {
    return (await settledCache(this.partyService.allParties$)) ?? [];
  }

  /**
   * Is this BAA/COI plausibly tied to the party? Either it shares a contract
   * with them (structural, trustworthy) or its party-ish text field matches the
   * party name (heuristic, for records with no contract link).
   */
  private linkedToParty(
    record: any,
    contractKeys: Set<string>,
    partyName: string,
    nameFields: string[]
  ): boolean {
    const linked = Array.isArray(record?.contracts) ? record.contracts : [];
    for (const entry of linked) {
      const key = this.str(typeof entry === 'object' ? entry?.contractId || entry?.contractNum : entry);
      if (key && contractKeys.has(key)) return true;
    }

    if (!partyName) return false;
    return nameFields.some((field) => {
      const value = this.normalizeName(this.readName(record?.[field]));
      return !!value && value === partyName;
    });
  }

  /** These fields hold either a plain string or a hydrated object with `.name`. */
  private readName(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.name || value.fullName || value.friendlyName || '';
  }

  private normalizeName(value: any): string {
    return this.str(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  private str(value: any): string {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  /** Same y/m/d boundary rule ContractService uses — expiring "today" isn't expired yet. */
  private isExpired(date: any): boolean {
    if (!date) return false;
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return false;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return parsed.getTime() < todayStart.getTime();
  }
}
